import * as React from 'react';
import { cn } from '@/lib/utils';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, hint, icon, iconRight, type, ...props }, ref) => {
    return (
      <div className="space-y-1.5 w-full">
        {label && (
          <label className="block text-[11px] font-bold uppercase tracking-[0.05em] text-[var(--text-3)] mb-1">
            {label}
          </label>
        )}
        <div className="relative group">
          {icon && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)] pointer-events-none z-10 transition-colors group-focus-within:text-[var(--brand)]">
              {icon}
            </span>
          )}
          <input
            type={type}
            ref={ref}
            className={cn(
              'flex h-[42px] w-full rounded-xl border bg-[var(--bg-1)] px-4 py-2 text-[14px] ring-offset-background',
              'text-[var(--text-1)] placeholder:text-[var(--text-3)] shadow-sm',
              'border-[var(--border-c)] outline-none transition-all duration-300',
              'hover:border-[var(--brand)] hover:shadow-md',
              'focus:border-[var(--brand)] focus:ring-4 focus:ring-[var(--brand-dim)] focus:shadow-lg',
              'disabled:cursor-not-allowed disabled:opacity-50',
              icon && 'pl-10',
              iconRight && 'pr-10',
              error && 'border-destructive focus:border-destructive focus:ring-red-500/10 focus:shadow-none',
              className
            )}
            {...props}
          />
          {iconRight && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-3)] transition-colors group-focus-within:text-[var(--brand)]">
              {iconRight}
            </span>
          )}
        </div>
        {error && <p className="text-[11px] font-medium text-destructive mt-1 ml-1 animate-in fade-in slide-in-from-top-1">{error}</p>}
        {hint && !error && <p className="text-[11px] text-[var(--text-3)] mt-1 ml-1">{hint}</p>}
      </div>
    );
  }
);
Input.displayName = 'Input';

export { Input };
